import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ActiveStatus,
  Frequency,
  SolutionGridItem,
  SolutionGridRequestDto,
  SolutionGridResponseDto,
  SolutionGridVersion,
} from '../types';
import {
  createSolutionGrid,
  getSolutionGridByTemplateId,
} from '../utils/httpRequest';
import { RootState } from '../store';
import { mapSolutionGrid } from '../utils/dataMapper';
import { isEqual } from 'lodash';

export interface SolutionGridState {
  sourceSolutionGrids: SolutionGridItem[];
  solutionGrids: SolutionGridItem[];
  versions?: SolutionGridVersion[];
  currentVersion?: SolutionGridVersion;
  loading: boolean;
  error?: string;
}

const initialState: SolutionGridState = {
  sourceSolutionGrids: [],
  solutionGrids: [],
  versions: [],
  loading: false,
};

export const fetchSolutionGrid = createAsyncThunk(
  'solutionGrid/fetchSolutionGrid',
  async ({
    templateID,
    versionID,
  }: {
    templateID: string;
    versionID?: string;
  }) => {
    const solutionGrids = await getSolutionGridByTemplateId(
      templateID,
      versionID
    );

    return solutionGrids;
  }
);

export const fetchCreateSolutionGrid = createAsyncThunk(
  'solutionGrid/fetchCreateSolutionGrid',
  async (payload: {
    templateID: string;
    solutionGrid: SolutionGridRequestDto;
  }) => {
    const solutionGrids = await createSolutionGrid(
      payload.solutionGrid,
      payload.templateID
    );

    return solutionGrids;
  }
);

export const solutionGridSlice = createSlice({
  name: 'solutionGrid',
  initialState,
  reducers: {
    setInitSolutionGridState: (
      state,
      action: PayloadAction<SolutionGridResponseDto>
    ) => {
      const {
        solutionGridDtoList,
        currentVersionDto,
        solutionGridVersionDtoList,
      } = action.payload;
      state.solutionGrids = solutionGridDtoList || [];
      state.sourceSolutionGrids = solutionGridDtoList || [];
      state.currentVersion = currentVersionDto;
      state.versions = solutionGridVersionDtoList || [];
    },
    setSolutionGrids: (state, action: PayloadAction<SolutionGridItem[]>) => {
      state.solutionGrids = action.payload;
    },
    resetSolutionGrids: (state) => {
      state.solutionGrids = [];
      state.sourceSolutionGrids = [];
      state.versions = [];
    },
    updateFrequency: (
      state,
      action: PayloadAction<{
        id: string;
        relevanceRatio: Frequency;
      }>
    ) => {
      const { id, relevanceRatio } = action.payload;
      const { solutionGrids } = state;

      state.solutionGrids = solutionGrids.map((i) =>
        i.gridID === id
          ? {
              ...i,
              relevanceRatio,
            }
          : i
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSolutionGrid.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSolutionGrid.fulfilled, (state, action) => {
        const {
          solutionGridVersionDtoList,
          currentVersionDto,
          solutionGridDtoList,
        } = action.payload.data;
        state.solutionGrids = solutionGridDtoList || [];
        state.sourceSolutionGrids = solutionGridDtoList || [];
        state.currentVersion = currentVersionDto;
        state.versions = solutionGridVersionDtoList || [];
        state.loading = false;
      })
      .addCase(fetchSolutionGrid.rejected, (state, action) => {
        state.error = 'Error';
        state.loading = false;
      })
      .addCase(fetchCreateSolutionGrid.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCreateSolutionGrid.fulfilled, (state, action) => {
        state.solutionGrids = action.payload.data;
        state.loading = false;
      })
      .addCase(fetchCreateSolutionGrid.rejected, (state, action) => {
        state.error = 'Create Solution Grid failed';
        state.loading = false;
      });
  },
});

export const {
  setSolutionGrids,
  updateFrequency,
  setInitSolutionGridState,
  resetSolutionGrids,
} = solutionGridSlice.actions;

export default solutionGridSlice.reducer;

export const selectSolutionGridVersions = (state: RootState) =>
  state.solutionGrids.versions;

export const selectCurrentSolutionGridVersions = (state: RootState) =>
  state.solutionGrids.currentVersion;

export const selectSolutionGrids = (state: RootState) =>
  state.solutionGrids.solutionGrids;

export const selectActiveSolutionGrids = (state: RootState) =>
  state.solutionGrids.solutionGrids.filter(
    (i) => i.activeStatus === ActiveStatus.ACTIVE
  );

export const selectInActiveSolutionGrids = (state: RootState) =>
  state.solutionGrids.solutionGrids.filter(
    (i) => i.activeStatus === ActiveStatus.INACTIVE
  );

export const selectSourceSolutionGrids = (state: RootState) =>
  state.solutionGrids.sourceSolutionGrids;

export const selectIsSolutionGridUpdated = (state: RootState) =>
  !isEqual(
    state.solutionGrids.solutionGrids,
    state.solutionGrids.sourceSolutionGrids
  );

export const selectIsFetchingSolutionGrid = (state: RootState) =>
  state.solutionGrids.loading;

export const selectMappedSolutionGrid = (state: RootState) => {
  const { solutionGrids } = state.solutionGrids;
  if (!solutionGrids?.length) {
    return [];
  }
  return mapSolutionGrid(solutionGrids);
};

export const selectMappedActiveSolutionGrid = (state: RootState) => {
  const { solutionGrids } = state.solutionGrids;
  if (!solutionGrids?.length) {
    return [];
  }
  return mapSolutionGrid(
    solutionGrids.filter((i) => i.activeStatus === ActiveStatus.ACTIVE)
  );
};
